/**
 * Save later on the web (docs/FAILURES.md "offline"), like the phone's queue (apps/mobile/src/offline): a check record
 * or a problem report (or the daily check-in) made while offline is kept in this browser and sent, in order, when the connection is back.
 * - Offline, a timeout or the server's own failure (5xx) keeps the job and stops the flush (the rest would fail too).
 * - A sign-in that expired (401) keeps the job for after sign-in.
 * - Any other refusal (4xx) moves it to "couldn't send", with the server's words, so it isn't retried forever.
 * Stored per person. Scans (photos of paper) aren't queued on the web: they need the photo, which stays on the device.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, newAttemptKey, type Api, type CheckinBody, type EntryBody, type FoodScope, type IncidentBody } from "../api.ts";
import type { DailyCheckin } from "@logwave/core";
import { isOffline, isRetryable } from "./failures.ts";

/**
 * `checkin`: the daily health check-in (LAUNCH-PROPOSALS item 6). `localOk` is core's checkinOk in this browser, so Do
 * can open marked "sending…" for an all-clear (never for a not-OK); `date` is the location's day it counts for. The
 * server's answer wins when it sends. The answers wait here only until then (per person), and are never drafted.
 */
export type OutboxPayload =
  | { kind: "entry"; body: EntryBody }
  | { kind: "incident"; body: IncidentBody }
  | { kind: "checkin"; body: CheckinBody; localOk: boolean; date: string }
  /** A food's photo taken offline (owner, 2026-10-08): the shrunk JPEG as base64, sent to that food when the connection is back. The row shows it from here meanwhile. */
  | { kind: "foodphoto"; scope: FoodScope; recordId: string; b64: string; /** The small picture for the roundup email. */ t64?: string };

/** A queued check-in sent: what this browser had shown, and what the server said (it wins). */
export interface CheckinSent { localOk: boolean; checkin: DailyCheckin }

/**
 * The save with its per-attempt key (audit #6): the caller's own key when it kept one across the person's retries (a
 * report's Send again), else a new one. A queued job keeps it, so every later send of that job is the same attempt and
 * the server saves it once.
 */
export function withAttemptKey(p: OutboxPayload): OutboxPayload {
  if (p.kind === "foodphoto" || p.body.idempotencyKey) return p; // a photo is stored by its own hash: sending it twice keeps one
  return { ...p, body: { ...p.body, idempotencyKey: newAttemptKey() } } as OutboxPayload;
}

/** The shrunk JPEG a queued photo holds, as a Blob. */
export function foodPhotoBlob(b64: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: "image/jpeg" });
}

function send(api: Api, orgId: string, p: OutboxPayload): Promise<unknown> {
  if (p.kind === "foodphoto") return api.addFoodPhoto(p.scope, p.recordId, foodPhotoBlob(p.b64), p.t64 ? foodPhotoBlob(p.t64) : undefined);
  return p.kind === "entry" ? api.createEntry(orgId, p.body) : p.kind === "incident" ? api.reportIncident(orgId, p.body) : api.checkin(orgId, p.body);
}
export interface OutboxJob { id: string; orgId: string; payload: OutboxPayload; label: string; createdAt: number; attempts: number; lastError?: string }
interface Stored { pending: OutboxJob[]; failed: OutboxJob[] }

export const OUTBOX_PREFIX = "cc.outbox.v1.";
export const STORAGE_FULL = "Not saved yet. This device's storage is full: free some space, or try again when you're online.";

export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void }

/** The queue itself, with no React: storage in, api out. */
export class WebOutbox {
  constructor(private store: StorageLike, private key: string, private now: () => number = () => Date.now()) {}

  read(): Stored {
    try {
      const raw = this.store.getItem(this.key);
      const s = raw ? (JSON.parse(raw) as Partial<Stored>) : {};
      return { pending: Array.isArray(s.pending) ? s.pending : [], failed: Array.isArray(s.failed) ? s.failed : [] };
    } catch { return { pending: [], failed: [] }; }
  }

  private write(s: Stored): void {
    try { this.store.setItem(this.key, JSON.stringify(s)); }
    catch { throw new Error(STORAGE_FULL); }
  }

  enqueue(orgId: string, payload: OutboxPayload, label: string): OutboxJob {
    const s = this.read();
    const job: OutboxJob = { id: `w_${this.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`, orgId, payload, label, createdAt: this.now(), attempts: 0 };
    this.write({ ...s, pending: [...s.pending, job] });
    return job;
  }

  dismiss(id: string): void {
    const s = this.read();
    this.write({ ...s, failed: s.failed.filter((j) => j.id !== id) });
  }

  /** Undo of a tap still waiting here (core corrections.ts U1): it's dropped and never sent. False: already sent. */
  drop(id: string): boolean {
    const s = this.read();
    if (!s.pending.some((j) => j.id === id)) return false;
    this.write({ ...s, pending: s.pending.filter((j) => j.id !== id) });
    return true;
  }

  /** Sends what waits, oldest first. Returns how many went. */
  async flush(api: Api): Promise<{ sent: number; failed: number; remaining: number; checkins: CheckinSent[] }> {
    let sent = 0, failed = 0;
    const checkins: CheckinSent[] = [];
    for (;;) {
      const s = this.read();
      const job = s.pending[0];
      if (!job) break;
      try {
        const result = await send(api, job.orgId, job.payload);
        if (job.payload.kind === "checkin") { const c = (result as { checkin?: DailyCheckin } | undefined)?.checkin; if (c) checkins.push({ localOk: job.payload.localOk, checkin: c }); }
        const now = this.read();
        this.write({ ...now, pending: now.pending.filter((j) => j.id !== job.id) });
        sent++;
      } catch (e) {
        const now = this.read();
        const msg = e instanceof Error ? e.message : String(e);
        // Held for the card (services/api heldentries.ts): the server kept it as this person's draft, so it leaves this
        // device's queue; the waiting line (components/HeldEntries.tsx) shows it, and it saves once the card is in.
        if (e instanceof ApiError && e.reason === "card_to_save" && (e.body as { held?: { kept?: boolean } } | undefined)?.held?.kept) {
          this.write({ ...now, pending: now.pending.filter((j) => j.id !== job.id) });
          sent++;
          continue;
        }
        if (isRetryable(e) || (e instanceof ApiError && e.status === 401)) {
          this.write({ ...now, pending: now.pending.map((j) => (j.id === job.id ? { ...j, attempts: j.attempts + 1, lastError: msg } : j)) });
          break;
        }
        this.write({ pending: now.pending.filter((j) => j.id !== job.id), failed: [...now.failed, { ...job, attempts: job.attempts + 1, lastError: msg }] });
        failed++;
      }
    }
    return { sent, failed, remaining: this.read().pending.length, checkins };
  }
}

export interface OutboxValue {
  pending: OutboxJob[];
  failed: OutboxJob[];
  /** Try now; offline = keep it here and send later. `queued: true` means it waits on this device. */
  saveOrQueue<T>(orgId: string, payload: OutboxPayload, label: string): Promise<{ result: T | null; queued: boolean; jobId?: string }>;
  flush(): Promise<void>;
  dismiss(id: string): void;
  /** Undo a queued job before it sends (it never reaches the server). False: it already went. */
  drop(id: string): boolean;
  /** The last queued check-in that sent (the server's answer; Do says it plainly when it differs). */
  checkinSent?: CheckinSent;
}

const OutboxContext = createContext<OutboxValue | null>(null);

/** Outside an OutboxProvider (tests of one screen): plain calls, nothing queued. */
function direct(api: Api | null): OutboxValue {
  return {
    pending: [], failed: [], flush: async () => {}, dismiss: () => {}, drop: () => false,
    saveOrQueue: async <T,>(orgId: string, p: OutboxPayload) => {
      if (!api) throw new Error("No connection to CleanCleaver.");
      const result = (await send(api, orgId, withAttemptKey(p))) as T;
      return { result, queued: false };
    },
  };
}

export function useOutbox(api: Api | null): OutboxValue {
  return useContext(OutboxContext) ?? direct(api);
}

const browserStorage: StorageLike = {
  getItem: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  setItem: (k, v) => localStorage.setItem(k, v),
};

export function OutboxProvider({ api, who, storage = browserStorage, children }: { api: Api; who: string; storage?: StorageLike; children: ReactNode }) {
  const box = useMemo(() => new WebOutbox(storage, `${OUTBOX_PREFIX}${who.trim().toLowerCase()}`), [storage, who]);
  const [state, setState] = useState<Stored>(() => box.read());
  const [checkinSent, setCheckinSent] = useState<CheckinSent>();
  const busy = useRef(false);
  const sync = useCallback(() => setState(box.read()), [box]);

  const flush = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try { const r = await box.flush(api); const c = r.checkins.at(-1); if (c) setCheckinSent(c); } finally { busy.current = false; sync(); }
  }, [box, api, sync]);

  useEffect(() => {
    sync();
    void flush();
    const on = () => void flush();
    window.addEventListener("online", on);
    return () => window.removeEventListener("online", on);
  }, [sync, flush]);

  const value = useMemo<OutboxValue>(() => ({
    pending: state.pending, failed: state.failed, flush, checkinSent,
    dismiss: (id) => { box.dismiss(id); sync(); },
    drop: (id) => { const ok = box.drop(id); sync(); return ok; },
    saveOrQueue: async <T,>(orgId: string, raw: OutboxPayload, label: string) => {
      const p = withAttemptKey(raw);
      try {
        const result = (await send(api, orgId, p)) as T;
        if (p.kind === "checkin") setCheckinSent(undefined); // a fresh answer from the server replaces any old notice
        return { result, queued: false };
      } catch (e) {
        if (!isOffline(e)) throw e;
        const job = box.enqueue(orgId, p, label); // throws STORAGE_FULL when the browser can't keep it
        sync();
        return { result: null, queued: true, jobId: job.id };
      }
    },
  }), [state, flush, box, api, sync, checkinSent]);

  return <OutboxContext.Provider value={value}>{children}</OutboxContext.Provider>;
}
